import { Injectable, inject } from '@angular/core';
import { enviarTokenFcm } from 'src/app/app.component';
import { SessionService } from 'src/app/services/session/session.service';
import { environment } from 'src/environments/environment.prod';

/** Lo que sabemos del permiso de notificaciones de este navegador. */
export type EstadoNotificacionesNavegador = 'granted' | 'denied' | 'default' | 'unsupported';

/**
 * Avisos «en el navegador» (31/10/2026). No hay canal propio en la API: el navegador es un
 * dispositivo más que recibe el `channel_push` de la alerta a través de su token FCM
 * (`user_fcmtokens`). Activarlo es, por tanto, pedir el permiso de notificaciones —siempre
 * dentro de un toque del usuario, nunca al cargar— y registrar el token de este navegador
 * con la misma función que usa `AppComponent` (`enviarTokenFcm`).
 */
@Injectable({ providedIn: 'root' })
export class NotificacionesNavegadorService {
  private session = inject(SessionService);

  /** La API del navegador, o `null` donde no existe (SSR, iOS fuera de la PWA…). */
  protected api(): { permission: NotificationPermission; requestPermission: () => Promise<NotificationPermission> } | null {
    return typeof Notification === 'undefined' ? null : Notification;
  }

  /**
   * iPhone/iPad sólo dan notificaciones web dentro de la app instalada (añadida a la
   * pantalla de inicio); en Safari normal no existe `Notification`. El iPad moderno se
   * anuncia como Mac, así que se distingue por la pantalla táctil.
   */
  esIos(nav: { userAgent: string; maxTouchPoints?: number } | null = typeof navigator === 'undefined' ? null : navigator): boolean {
    if (!nav) return false;
    return /iPhone|iPad|iPod/i.test(nav.userAgent) || (/Macintosh/i.test(nav.userAgent) && (nav.maxTouchPoints || 0) > 1);
  }

  permiso(): EstadoNotificacionesNavegador {
    const api = this.api();
    return api ? api.permission : 'unsupported';
  }

  /**
   * Pide el permiso (si no se ha decidido ya) y, concedido, registra el token de este
   * navegador en la API. Un permiso bloqueado no se vuelve a preguntar: el navegador no
   * enseñaría nada y sólo se puede cambiar desde su configuración.
   */
  async activar(): Promise<EstadoNotificacionesNavegador> {
    const api = this.api();
    if (!api) return 'unsupported';

    let estado: EstadoNotificacionesNavegador = api.permission;
    if (estado === 'default') {
      try {
        estado = await api.requestPermission();
      } catch {
        estado = 'unsupported';
      }
    }

    if (estado === 'granted') void this.registrarToken();
    return estado;
  }

  /** Obtiene el token FCM de este navegador y lo liga a la cuenta. Los fallos no rompen nada. */
  async registrarToken(): Promise<boolean> {
    try {
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, getToken } = await import('firebase/messaging');

      if (!getApps().length) initializeApp(environment.firebase);
      const token = await getToken(getMessaging(), { vapidKey: environment.firebase.vapidKey });
      return token ? await enviarTokenFcm(this.session.mc, token, { forzar: true }) : false;
    } catch (error) {
      console.warn('[push] no se pudo registrar este navegador', error);
      return false;
    }
  }
}
